import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Calendar, PlayCircle, CheckCircle2, Clock, Wind, AlertTriangle, Home, UserCheck, Flag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import WorkerTicketDialog from "@/components/worker-ticket-dialog";

type LaundryItem = { name: string; qty: number };
type LaundryJob = {
  id: number;
  status: string;
  cleaningJobId: number | null;
  apartmentName: string | null;
  apartmentId: number | null;
  takenItems: LaundryItem[] | null;
  returnedItems: LaundryItem[] | null;
  mismatch: boolean;
  mismatchDetails: { name: string; taken: number; returned: number }[] | null;
  notes: string | null;
  scheduledAt: string | null;
  assignedTo: number | null;
  assignedToName: string | null;
};

type TicketPrefill = {
  apartmentId: number | null;
  cleaningJobId: number | null;
  apartmentName: string | null;
  category?: string;
  laundryJobId?: number | null;
  endpoint?: 'cleaner' | 'laundry';
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  pending:     { label: "Pending",     variant: "secondary" },
  in_progress: { label: "In Progress", variant: "default" },
  completed:   { label: "Completed",   variant: "secondary" },
};

function ReturnDialog({ job, onClose, onSubmit, isPending }: {
  job: LaundryJob;
  onClose: () => void;
  onSubmit: (items: LaundryItem[]) => void;
  isPending: boolean;
}) {
  const takenItems = job.takenItems ?? [];
  const [quantities, setQuantities] = useState<number[]>(takenItems.map(i => i.qty));

  const setQty = (idx: number, val: number) =>
    setQuantities(prev => prev.map((q, i) => i === idx ? Math.max(0, Math.min(val, takenItems[idx]?.qty ?? val)) : q));

  const items: LaundryItem[] = takenItems.map((item, i) => ({ name: item.name, qty: quantities[i] ?? 0 }));
  const valid = items.length > 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log Returned Items</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Enter how many of each item you're returning. Anything less than what was sent triggers a mismatch alert.
        </p>

        {takenItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">No items recorded for this job.</p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_80px_80px] gap-2 text-xs font-medium text-muted-foreground px-1">
              <span>Item</span>
              <span className="text-center">Sent</span>
              <span className="text-center">Returning</span>
            </div>
            {takenItems.map((item, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_80px] items-center gap-2 bg-muted/40 rounded-md px-3 py-2">
                <span className="text-sm font-medium">{item.name}</span>
                <span className="text-center text-sm text-muted-foreground">{item.qty}</span>
                <Input
                  type="number"
                  min={0}
                  max={item.qty}
                  value={quantities[i] ?? item.qty}
                  onChange={e => setQty(i, Number(e.target.value))}
                  className="h-8 text-center"
                />
              </div>
            ))}
          </div>
        )}

        {job.notes && <p className="text-xs text-muted-foreground border-t pt-2">{job.notes}</p>}

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(items.filter(i => i.qty > 0))} disabled={!valid || isPending}>
            {isPending ? "Submitting…" : "Complete & Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LaundryJobCard({ job, currentUserId, onStart, onComplete, onReport }: {
  job: LaundryJob;
  currentUserId: number | null;
  onStart: (id: number) => void;
  onComplete: (job: LaundryJob) => void;
  onReport: (job: LaundryJob) => void;
}) {
  const cfg = statusConfig[job.status] ?? { label: job.status, variant: "outline" as const };
  const locationLabel = job.apartmentName ?? (job.apartmentId ? `Unit #${job.apartmentId}` : null);
  const claimedBySomeoneElse = job.assignedTo != null && currentUserId != null && job.assignedTo !== currentUserId;

  return (
    <Card className={`border ${job.mismatch ? "border-destructive/50" : "border-border/50"}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={cfg.variant}>{cfg.label}</Badge>
            {locationLabel && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Home className="w-3 h-3" />
                {locationLabel}
              </div>
            )}
            {claimedBySomeoneElse && (
              <Badge variant="outline" className="flex items-center gap-1">
                <UserCheck className="w-3 h-3" />
                {job.assignedToName ?? `User #${job.assignedTo}`}
              </Badge>
            )}
          </div>
          {job.mismatch && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Mismatch
            </Badge>
          )}
        </div>

        {job.scheduledAt && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <span>{format(new Date(job.scheduledAt), "MMM d, yyyy · h:mm a")}</span>
          </div>
        )}

        {job.takenItems && job.takenItems.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Items to launder</p>
            <div className="flex flex-wrap gap-1">
              {job.takenItems.map((item, i) => (
                <span key={i} className="text-xs bg-muted rounded px-1.5 py-0.5">{item.name} ×{item.qty}</span>
              ))}
            </div>
          </div>
        )}

        {job.returnedItems && job.returnedItems.length > 0 && (
          <div className="space-y-1 border-t pt-2">
            <p className="text-xs font-medium text-muted-foreground">Returned</p>
            <div className="flex flex-wrap gap-1">
              {job.returnedItems.map((item, i) => (
                <span key={i} className="text-xs bg-muted rounded px-1.5 py-0.5">{item.name} ×{item.qty}</span>
              ))}
            </div>
          </div>
        )}

        {job.mismatch && job.mismatchDetails && (
          <div className="space-y-1 bg-destructive/5 rounded-lg p-2">
            <p className="text-xs font-medium text-destructive">Mismatch details</p>
            {job.mismatchDetails.map((d, i) => (
              <p key={i} className="text-xs text-destructive">
                {d.name}: sent {d.taken}, returned {d.returned}
              </p>
            ))}
          </div>
        )}

        {job.status !== "completed" && (
          <div className="flex gap-2 pt-1 flex-wrap">
            {job.status === "pending" && (
              <Button size="sm" className="flex-1 min-w-[120px]" onClick={() => onStart(job.id)}>
                <PlayCircle className="w-4 h-4 mr-1.5" /> Start
              </Button>
            )}
            {job.status === "in_progress" && (
              <Button size="sm" variant="outline" className="flex-1 min-w-[160px]" onClick={() => onComplete(job)}>
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete & Return Items
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onReport(job)}>
              <Flag className="w-4 h-4 mr-1.5" /> Report Issue
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function WorkerLaundryTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const currentUserId = (user as any)?.id ?? null;
  const [returningJob, setReturningJob] = useState<LaundryJob | null>(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketPrefill, setTicketPrefill] = useState<TicketPrefill | null>(null);

  const { data: jobs = [], isLoading } = useQuery<LaundryJob[]>({
    queryKey: ["/api/laundry/jobs"],
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, returnedItems }: { id: number; status: string; returnedItems?: LaundryItem[] }) => {
      const res = await apiRequest("PUT", `/api/laundry/jobs/${id}`, { status, returnedItems });
      return res.json();
    },
    onSuccess: (data: LaundryJob) => {
      queryClient.invalidateQueries({ queryKey: ["/api/laundry/jobs"] });
      if (data.mismatch) {
        toast({ title: "Mismatch Detected", description: "Admin has been notified.", variant: "destructive" });
      } else {
        toast({ title: "Laundry job completed" });
      }
      setReturningJob(null);
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const openReportForJob = (job: LaundryJob) => {
    setTicketPrefill({
      apartmentId: job.apartmentId,
      cleaningJobId: job.cleaningJobId,
      apartmentName: job.apartmentName,
      laundryJobId: job.id,
      category: 'cleaning_issue',
      endpoint: 'laundry',
    });
    setTicketOpen(true);
  };

  const openReportStandalone = () => {
    setTicketPrefill({
      apartmentId: null,
      cleaningJobId: null,
      apartmentName: null,
      category: 'general',
      endpoint: 'laundry',
    });
    setTicketOpen(true);
  };

  const sortByScheduledAt = (arr: LaundryJob[]) =>
    [...arr].sort((a, b) => (a.scheduledAt ? new Date(a.scheduledAt).getTime() : Infinity) - (b.scheduledAt ? new Date(b.scheduledAt).getTime() : Infinity));

  const active = sortByScheduledAt(jobs.filter(j => j.status !== "completed"));
  const done   = sortByScheduledAt(jobs.filter(j => j.status === "completed"));

  if (isLoading) return (
    <div className="space-y-3">
      {[1, 2].map(i => <Skeleton key={i} className="h-32 w-full" />)}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {jobs.length === 0 ? "No laundry jobs in the queue." : `${active.length} active · ${done.length} done`}
        </p>
        <Button size="sm" variant="outline" onClick={openReportStandalone}>
          <Flag className="w-4 h-4 mr-1.5" /> Report Issue
        </Button>
      </div>

      {jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
          <Wind className="w-10 h-10 opacity-30" />
          <p className="text-sm">No laundry jobs in the queue right now.</p>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Clock className="w-4 h-4" /> Active ({active.length})
              </div>
              {active.map(j => (
                <LaundryJobCard
                  key={j.id}
                  job={j}
                  currentUserId={currentUserId}
                  onStart={id => updateMutation.mutate({ id, status: "in_progress" })}
                  onComplete={job => setReturningJob(job)}
                  onReport={openReportForJob}
                />
              ))}
            </section>
          )}
          {done.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <CheckCircle2 className="w-4 h-4" /> Done ({done.length})
              </div>
              {done.map(j => (
                <LaundryJobCard
                  key={j.id}
                  job={j}
                  currentUserId={currentUserId}
                  onStart={() => void 0}
                  onComplete={() => void 0}
                  onReport={openReportForJob}
                />
              ))}
            </section>
          )}
        </>
      )}

      {returningJob && (
        <ReturnDialog
          job={returningJob}
          onClose={() => setReturningJob(null)}
          onSubmit={items => updateMutation.mutate({ id: returningJob.id, status: "completed", returnedItems: items })}
          isPending={updateMutation.isPending}
        />
      )}

      <WorkerTicketDialog
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        prefill={ticketPrefill}
      />
    </div>
  );
}
